import { Button, Card, Checkbox, Col, DatePicker, Form, Icon, Input, InputNumber, Row, Select, TimePicker } from 'antd'
import useFetchSelect from '../../../../elements/Hooks/useFetchSelect'
import DocumentsApi from '../../../../api/Documents'
import { VendorQuoteApi } from '../../../../api/Purchase/VendorQuotes'
import { AddQuoteContext } from './AddQuoteCont'
import FinanceBreakDown from '../../../../components/Sales/Quotes/AddQuote/FinanceBreakDown'
import { getDifferenceValue } from './quoteLineReducer'
import CustomSpin from '../../../../elements/CustomSpin'
import useFetchList from '../../../../elements/Hooks/useFetchList'
import useFetchObject from '../../../../elements/Hooks/useFetchObject'
import useFetchSelectUrl from '../../../../elements/Hooks/useFetchSelectUrl'
import { useForceUpdate } from '../../../../elements/Hooks/useForceUpdate'
import InfoText from '../../../../elements/InfoText'
import MaterialDetails from '../../../../elements/MaterialDetails'
import ProductSelection from '../../../../elements/ProductSelection'
import CurrencySelect from '../../../../elements/Select/Currency'
import TaxTypeSelect from '../../../../elements/Select/Lines/TaxType'
import { get, isEmpty, max, maxBy, round, sumBy } from 'lodash'
import {
  defaultFormItemLayout,
  LabelItemLayout,
  fullWidthFormItemLayout,
  rules,
  threeFormItemLayout,
} from '../../../../modules/FormItems'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { compose } from 'recompose'
import { PaymentTermsApi } from '../../../../api/People/PaymentTerms'
import withSort from '../../../../elements/HOC/withSort'
import withSpinning from '../../../../elements/HOC/withSpinning'
import InfoTooltip from '../../../../elements/InfoTooltip'
import CustomSelect from '../../../../elements/Select/CustomSelect'
import LocationSelect from '../../../../elements/Select/Location'
import withUrlFetch from '../../../../elements/Select/withUrlFetch'
import ShippingDetails from '../../../../elements/ShippingDetails'
import { dateVal, moneyInline, numVal, strVal } from '../../../../modules/Utils'
import FormSectionTitle from '../../../../elements/FormSectionTitle'

const docsApi = new DocumentsApi()
const { TextArea } = Input

const VehiclesSelect = compose(
  withUrlFetch({
    name: 'vehicles',
    url: 'vehicles?select=*',
    cache: true,
    forceFetch: true,
  }),
  withSpinning,
  withSort
)(CustomSelect)

function calcMargin(value = 0, margin = 0, type = '+') {
  if (margin === 0) return parseInt(value)
  return round((value || 0) / (1 - (margin || 0) / 100), 2)
}

const mapMargin = {
  MRO: 0,
  Fencing: 45,
}

const mapFinMargin = {
  MRO: 0,
  Fencing: 25,
}

function sortPaymentsTerms(list = []) {
  const paymentTermsOrder = ['Deposit', 'Days After Order', 'On Delivery', 'Days After Delivery']

  return list.sort(function (a, b) {
    return paymentTermsOrder.indexOf(get(a, 'due.type')) - paymentTermsOrder.indexOf(get(b, 'due.type'))
  })
}

function QuoteLineForm(props) {
  const { getFieldDecorator, getFieldValue, resetFields, setFieldsValue, getFieldsValue } = props.form

  const { quote, line, addDestination } = React.useContext(AddQuoteContext)

  const company = quote.company._id

  const isHeader = getFieldValue('isHeader')
  const isLookup = getFieldValue('isVQLookUp')
  const isProduct = getFieldValue('isProduct')
  const [category, setCategory] = useState({})
  const [total, setTotal] = useState(line.amount || { gross: 0, net: 0, tax: 0 })
  const productType = getFieldValue('productType')
  const group = getFieldValue('group._id')
  const [vendorQuoteLine, setVendorQuoteLine] = useState(get(line, 'vendorQuoteLine', {}))

  const [showFinanceCost, setShowFinanceCost] = React.useState(false)

  React.useEffect(() => {
    setShowFinanceCost(getFieldValue('buildUp.finance.difference') !== 0)
  }, [getFieldValue('buildUp.finance.difference')])

  const { SelectData: HeaderLinesSelect } = useFetchSelect({
    url: `quotes/${quote._key}/lines/?isHeader=true`,
    dependencyArray: [quote],
    dontFetch: isEmpty(quote),
  })

  const { SelectData: VendorQuoteLinesSelect } = useFetchSelect({
    url: `quotes/${quote._key}/vendorQuoteLines/`,
    dependencyArray: [quote],
    dontFetch: isEmpty(quote),
    mapFunction: (i) => ({
      ...i,
      display: `${i.spquote.vendor.fn}: [Quote No: ${i.spquote.quoteNo}] - Line No: ${i.no} - ${i.name} `,
    }),
  })

  const { list: vendorQuotePaymentTermsLines, fetching: fetchingVendorQuotePaymentTermsLines } = useFetchList(
    () => PaymentTermsApi.list(get(vendorQuoteLine, 'spquote._id')),
    [get(vendorQuoteLine, 'spquote._id')],
    isEmpty(get(vendorQuoteLine, 'spquote._id'))
  )

  const { list: QuotePaymentTermsLines, fetching: fetchingQuotePaymentTermsLines } = useFetchList(
    () => PaymentTermsApi.list(quote._id),
    [quote._id],
    isEmpty(quote._id)
  )

  const { object: vendorQuote, fetching: fetchingVendorQuote } = useFetchObject(
    () => VendorQuoteApi.read(get(vendorQuoteLine, 'spquote._id', '').replace('spquotes/', '')),
    false,
    [vendorQuoteLine],
    isEmpty(get(vendorQuoteLine, 'spquote._id'))
  )

  const { object: conversion, fetching: fetchingCurrency } = useFetchObject(
    () =>
      VendorQuoteApi.conversion({
        fromCurrency: getFieldValue('from.currency') || get(vendorQuoteLine, 'spquote.currency'),
        toCurrency: getFieldValue('to.currency') || quote.currency,
        rate: vendorQuoteLine.rate,
      }),
    false,
    [vendorQuoteLine, getFieldValue('from.currency'), getFieldValue('to.currency')],
    numVal(vendorQuoteLine, 'rate') === 0 || isEmpty(vendorQuoteLine)
  )

  useEffect(() => {
    if (!isEmpty(vendorQuoteLine)) {
      setFieldsValue({
        vendorNo: get(vendorQuoteLine, 'vendorNo'),
        'materials.oem.name': get(vendorQuoteLine, 'materials.oem.name'),
        'materials.oem.no': get(vendorQuoteLine, 'materials.oem.no'),
        'shipping.weights.net': get(vendorQuoteLine, 'shipping.weights.net'),
        'shipping.weights.scale.code': get(vendorQuoteLine, 'shipping.weights.scale.code'),
        'shipping.volume.volume': get(vendorQuoteLine, 'shipping.volume.volume'),
        'shipping.dimensions.height': get(vendorQuoteLine, 'shipping.dimensions.height'),
        'shipping.dimensions.length': get(vendorQuoteLine, 'shipping.dimensions.length'),
        'shipping.dimensions.width': get(vendorQuoteLine, 'shipping.dimensions.width'),
        'shipping.dimensions.scale.code': get(vendorQuoteLine, 'shipping.dimensions.scale.code'),
        'shipping.hsCode': get(vendorQuoteLine, 'shipping.hsCode'),
        'shipping.stackable': get(vendorQuoteLine, 'shipping.stackable'),
        'shipping.country.origin': get(vendorQuoteLine, 'shipping.country.origin'),
        isProduct: get(vendorQuoteLine, 'isProduct'),
        productType: get(vendorQuoteLine, 'productType'),
        'group._id': get(vendorQuoteLine, 'group._id'),
        'category._id': get(vendorQuoteLine, 'category._id'),
        'product._id': get(vendorQuoteLine, 'product._id'),
        'productLine._id': get(vendorQuoteLine, 'productLine._id'),
      })
    }
  }, [getFieldValue('vendorQuoteLine._id'), vendorQuoteLine])

  useEffect(() => {
    if (!isEmpty(conversion)) {
      const isSameVQL = get(vendorQuoteLine, '_rev') === get(line, 'vendorQuoteLine._rev')
      const isSameConversion = get(line, 'buildUp.cost.conversion') === getFieldValue('buildUp.cost.conversion')

      if (isSameVQL && isSameConversion) {
        setFieldsValue({
          'buildUp.cost.conversion':
            (getFieldValue('isCustomConversion') && round(numVal(conversion, 'rate'), 3)) ||
            get(line, 'buildUp.cost.conversion'),
          'buildUp.finance.converted': get(line, 'buildUp.finance.converted'),
          'buildUp.cost.cost': get(line, 'buildUp.cost.cost'),
        })
      } else {
        const conv = getFieldValue('isCustomConversion')
          ? getFieldValue('buildUp.cost.conversion')
          : round(numVal(conversion, 'rate'), 3) || 1
        const amount = round(numVal(vendorQuoteLine, 'rate') * conv, 2) || 0
        setFieldsValue({
          'buildUp.cost.conversion': conv,
          'buildUp.finance.converted': amount,
          'buildUp.cost.cost': amount,
        })
      }
    }
  }, [
    conversion,
    getFieldValue('buildUp.cost.conversion'),
    getFieldValue('isCustomConversion'),
    get(vendorQuote, '_id'),
    get(vendorQuoteLine, 'rate'),
  ])

  const buildUpFinanceConverted = getFieldValue('buildUp.finance.converted')
  React.useEffect(() => {
    setFieldsValue({
      'buildUp.cost.cost': buildUpFinanceConverted,
    })
  }, [buildUpFinanceConverted])

  const buildUpCostMargin = getFieldValue('buildUp.cost.margin') || 0
  const buildUpCostCost = getFieldValue('buildUp.cost.cost') || 0
  const buildUpFinanceMargin = getFieldValue('buildUp.finance.margin') || 0
  const buildUpFinanceCost = getFieldValue('buildUp.finance.difference') || 0
  const buildUpOtherMargin = getFieldValue('buildUp.other.margin') || 0
  const buildUpOtherCost = getFieldValue('buildUp.other.cost') || 0
  const unit = getFieldValue('unit') || 0
  const taxPercentage = getFieldValue('tax.percentage') || 0

  function calculate() {
    const itemSell = calcMargin(buildUpCostCost, buildUpCostMargin)
    const financeSell = calcMargin(buildUpFinanceCost, buildUpFinanceMargin)
    const otherSell = calcMargin(buildUpOtherCost, buildUpOtherMargin)
    const subTotal = itemSell + financeSell + otherSell
    const taxRate = round(subTotal * (taxPercentage / 100), 3 )

    setFieldsValue({
      'buildUp.cost.sell': itemSell,
      'buildUp.finance.sell': financeSell,
      'buildUp.other.sell': otherSell,
      rate: subTotal,
      'tax.rate': taxRate,
    })


    const gross = round(unit * subTotal, 2)
    const tax = round(gross * (taxPercentage / 100), 2)
    const net = round(gross + tax, 2)
    setTotal({ gross, tax, net })
  }

  React.useEffect(() => {
    calculate()
  }, [
    buildUpCostMargin,
    buildUpCostCost,
    buildUpFinanceMargin,
    buildUpFinanceCost,
    buildUpOtherMargin,
    buildUpOtherCost,
    unit,
    taxPercentage,
  ])

  const DEPOSIT = 'Deposit'
  const DARO = 'Days After Order'
  const DADO = 'Days After Delivery'
  const OD = 'On Delivery'

  function getCreditDays(dueType, timelineEnd, deliveryNo, dueDays, type) {
    switch (dueType) {
      case DEPOSIT:
        return timelineEnd
      case OD:
        return timelineEnd - deliveryNo
      case DADO:
        return Math.abs(deliveryNo + dueDays - timelineEnd)
      case DARO:
        return dueDays
    }
  }

  function _addDataToPaymentTerms(pt, timelineEnd, deliveryNo, type) {
    const dueDays = numVal(pt, 'due.days')
    const creditDays = getCreditDays(pt.due.type, timelineEnd, deliveryNo, dueDays, type)

    const financeRatePerCentPerDay = (getFieldValue('buildUp.finance.rate') || 12) / 100 / 365

    const truePercentage = pt.percentage / 100
    const amount = truePercentage * vendorQuoteLine.rate
    const finance = financeRatePerCentPerDay * creditDays * amount

    return {
      ...pt,
      amount,
      creditDays,
      finance,
    }
  }

  function preparePaymentTermLines() {
    let clientPaymentTermsLines = sortPaymentsTerms(QuotePaymentTermsLines)
    let vendorPaymentTermsLines = sortPaymentsTerms(vendorQuotePaymentTermsLines)

    const clientDeliveryNo = numVal(quote, 'delivery.no')
    const vendorDeliveryNo = numVal(vendorQuote, 'delivery.no')

    const clientDADOLines = clientPaymentTermsLines.filter((p) => p.due.type === DADO)
    const clientDADOMaxDay = maxBy(clientDADOLines, 'due.days')
    const clientDADO = numVal(clientDADOMaxDay, 'due.days')

    const vendorDADOLines = vendorPaymentTermsLines.filter((p) => p.due.type === DADO)
    const VendorDADOMaxDay = maxBy(vendorDADOLines, 'due.days')
    const vendorDADO = numVal(VendorDADOMaxDay, 'due.days')

    const timelineClient = clientDeliveryNo + clientDADO
    const timelineVendor = vendorDeliveryNo + vendorDADO
    const timelineEnd = max([timelineVendor, timelineClient])

    vendorPaymentTermsLines = vendorPaymentTermsLines.map((vpt) =>
      _addDataToPaymentTerms(vpt, timelineEnd, vendorDeliveryNo, 'vendor')
    )
    const totalVendorCreditDays = sumBy(vendorPaymentTermsLines, 'creditDays')
    vendorPaymentTermsLines = vendorPaymentTermsLines.map((pt) => {
      return {
        ...pt,
        creditPercentage: totalVendorCreditDays === 0 ? 0 : pt.creditDays / totalVendorCreditDays,
      }
    })

    clientPaymentTermsLines = clientPaymentTermsLines.map((cpt) =>
      _addDataToPaymentTerms(cpt, timelineEnd, clientDeliveryNo, 'client')
    )
    const totalClientCreditDays = sumBy(clientPaymentTermsLines, 'creditDays')
    clientPaymentTermsLines = clientPaymentTermsLines.map((pt) => {
      return {
        ...pt,
        creditPercentage: totalClientCreditDays === 0 ? 0 : pt.creditDays / totalClientCreditDays,
      }
    })

    return {
      vendorPaymentTermsLines,
      totalVendorCreditDays,
      clientPaymentTermsLines,
      totalClientCreditDays,
      clientDeliveryNo,
      vendorDeliveryNo,
      clientDADOLines,
      clientDADOMaxDay,
      clientDADO,
      timelineEnd,
    }
  }

  const forceUpdate = useForceUpdate()
  const [financeBreakDown, setFinanceBreakDown] = useState({})

  React.useEffect(() => {
    if (!!vendorQuoteLine) {
      const {
        vendorPaymentTermsLines,
        totalVendorCreditDays,
        clientPaymentTermsLines,
        totalClientCreditDays,
        clientDeliveryNo,
        vendorDeliveryNo,
        clientDADOLines,
        clientDADOMaxDay,
        clientDADO,
        timelineEnd,
      } = preparePaymentTermLines()

      //finance
      const fcost =
        round(
          vendorPaymentTermsLines.reduce((i, c) => i + c.finance, 0),
          2
        ) || 0
      const fcredit =
        round(
          clientPaymentTermsLines.reduce((i, c) => i + c.finance, 0),
          2
        ) || 0
      const finance = {
        cost: fcost,
        credit: fcredit,
        difference: getDifferenceValue(fcost, fcredit),
      }

      setFinanceBreakDown({
        vendorPaymentTermsLines,
        totalVendorCreditDays,
        clientPaymentTermsLines,
        totalClientCreditDays,
        clientDeliveryNo,
        vendorDeliveryNo,
        clientDADOLines,
        clientDADOMaxDay,
        clientDADO,
        timelineEnd,
        finance,
      })

      setFieldsValue({
        'buildUp.finance.cost': finance.cost * round(numVal(conversion, 'rate'), 3),
        'buildUp.finance.credit': finance.credit * round(numVal(conversion, 'rate'), 3),
        'buildUp.finance.difference': finance.difference * round(numVal(conversion, 'rate'), 3),
      })
    }
    forceUpdate()
  }, [
    getFieldValue('buildUp.cost.conversion'),
    conversion,
    get(conversion, 'rate'),
    get(vendorQuoteLine, 'rate'),
    fetchingVendorQuote,
    get(vendorQuoteLine, '_rev', ''),
    fetchingVendorQuotePaymentTermsLines,
    fetchingQuotePaymentTermsLines,
    getFieldValue('buildUp.finance.rate'),
  ])

  const { SelectData: VendorQuoteSelect } = useFetchSelect({
    url: `/vendorQuotes?select=_id,quoteNo,title&status=Confirmed`,
    mapFunction: ({ quoteNo, title, ...quote }) => ({
      display: `[${quoteNo}] ${title}`,
      ...quote,
    }),
  })

  const VquoteId = (getFieldValue('vendorQuote._id') || '').replace('spquotes/', '')

  const { SelectData: VendorQuoteLinesLookupSelect } = useFetchSelect({
    url: `/vendorQuotes/lines/${VquoteId}`,
    dependencyArray: [VquoteId],
    dontFetch: isEmpty(VquoteId),
  })

  const isNoBid = getFieldValue('isNoBid')

  return (
    <Form>
      <Row>
        <Col span={12}>
          <Form.Item {...defaultFormItemLayout} label={'Is Header Line'}>
            {getFieldDecorator('isHeader', {
              initialValue: get(line, 'isHeader'),
              valuePropName: 'checked',
            })(<Checkbox />)}
          </Form.Item>
        </Col>
        {!isHeader && (
          <Col span={12}>
            <Form.Item {...defaultFormItemLayout} label={'Header Line'}>
              {getFieldDecorator('parentId', {
                initialValue: get(line, 'parentId'),
              })(<HeaderLinesSelect keyAtr={'_id'} valueAtr={'name'} />)}
            </Form.Item>
          </Col>
        )}
      </Row>
      {isHeader ? (
        <Row>
          <Col span={12}>
            <Form.Item {...defaultFormItemLayout} label={'Line No.'}>
              {getFieldDecorator('no', {
                initialValue: get(line, 'no'),
                rules: rules.input,
              })(<Input placeholder={'Line No.'} />)}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item {...defaultFormItemLayout} label={'Line Name'}>
              {getFieldDecorator('name', {
                initialValue: get(line, 'name'),
                rules: rules.input,
              })(<Input placeholder={'Line Name'} />)}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item {...defaultFormItemLayout} label={'Description'}>
              {getFieldDecorator('description', {
                initialValue: get(line, 'description'),
              })(<TextArea placeholder={'Description'} />)}
            </Form.Item>
          </Col>
        </Row>
      ) : (
        <>
          <Row>
            <Col span={12}>
              <Form.Item {...defaultFormItemLayout} label={'VQ Lookup'}>
                {getFieldDecorator('isVQLookUp', {
                  initialValue: get(line, 'isVQLookUp'),
                  valuePropName: 'checked',
                })(<Checkbox />)}
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item {...defaultFormItemLayout} label={'No Bid'}>
                {getFieldDecorator('isNoBid', {
                  initialValue: get(line, 'isNoBid'),
                  valuePropName: 'checked',
                })(<Checkbox />)}
              </Form.Item>
            </Col>

            {isLookup ? (
              <Row>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label="Vendor Quotes">
                    {getFieldDecorator('vendorQuote._id', {
                      initialValue: get(line, 'vendorQuote._id'),
                      // rules: rules.select,
                    })(
                      <VendorQuoteSelect
                        keyAtr={'_id'}
                        valueAtr={'display'}
                        placeholder="Select a Quote"
                        style={{ width: '100%' }}
                      />
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label="Vendor Quotes Line">
                    {getFieldDecorator('vendorQuoteLine._id', {
                      initialValue: get(line, 'vendorQuoteLine._id'),
                      // rules: rules.select,
                    })(
                      <VendorQuoteLinesLookupSelect
                        keyAtr={'_id'}
                        setSelected={setVendorQuoteLine}
                        // onChange={onChangeVQL}
                        valueAtr={'name'}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            ) : (
              <>
                <Col span={24}>
                  {/**{getFieldValue('product._id') && (**/}
                  <Form.Item {...fullWidthFormItemLayout} label={'Vendor Line'}>
                    {getFieldDecorator('vendorQuoteLine._id', {
                      initialValue: get(line, 'vendorQuoteLine._id'),
                    })(
                      <VendorQuoteLinesSelect
                        style={{ width: '80%' }}
                        placeholder={'Vendor Quote Line'}
                        keyAtr={'_id'}
                        valueAtr={'display'}
                        setSelected={setVendorQuoteLine}
                      />
                    )}
                    <Link
                      target={'_blank'}
                      to={`/purchasing/vendor/quotes/${get(vendorQuoteLine, 'spquote._id', '').replace(
                        'spquotes/',
                        ''
                      )}/update`}>
                      <Icon style={{ marginLeft: 5 }} type="fullscreen" />
                    </Link>
                    <InfoTooltip
                      title={
                        'If there are no lines, That means there is no Vendor Quote with the same enquiry as this Quote'
                      }
                    />
                  </Form.Item>
                  {/*)}*/}
                </Col>
              </>
            )}
          </Row>

          <Row>
            <h3>Client Quote Details</h3>
            <Col span={6}>
              <Form.Item {...defaultFormItemLayout} label="Line No">
                {getFieldDecorator('no', {
                  initialValue: get(line, 'no'),
                  rules: rules.input,
                })(<Input placeholder={'Line No'} style={{ width: '80%' }} />)}
                <InfoText text={`Vendor Quote info : ${get(vendorQuoteLine, 'no', '...')}.`} noMarginTop={true} />
              </Form.Item>
            </Col>
            <Col span={18}>
              <Form.Item {...defaultFormItemLayout} label={'Line Name'}>
                {getFieldDecorator('name', {
                  initialValue: get(line, 'name'),
                })(<Input placeholder={'Line Name'} />)}
                <InfoText
                  text={`Vendor Quote Line Name : ${get(vendorQuoteLine, 'name', '...')}.`}
                  noMarginTop={true}
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item {...fullWidthFormItemLayout} label={'Description'}>
                {getFieldDecorator('description', {
                  initialValue: get(line, 'description'),
                })(<TextArea placeholder={'Description'} />)}
                <InfoText
                  text={`Vendor Quote Description : ${get(vendorQuoteLine, 'description', '...')}.`}
                  noMarginTop={true}
                />
              </Form.Item>
            </Col>

            {get(quote, 'delivery.type') !== 'N/A' && (
              <Col span={8}>
                <Form.Item {...defaultFormItemLayout} label="Delivery Days">
                  {getFieldDecorator('delivery.no', {
                    initialValue: get(line, 'delivery.no') || get(quote, 'delivery.no'),
                    rules: rules.select,
                  })(<InputNumber placeholder={'Delivery Days'} />)}
                  <InfoText
                    text={`Vendor Quote  Delivery Days : ${get(vendorQuoteLine, 'delivery.no', '...')}.`}
                    noMarginTop={true}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          <MaterialDetails
            line={line}
            {...{
              getFieldDecorator,
              getFieldValue,
              setFieldsValue,
              resetFields,
              type: 'Quote',
            }}
          />

          <ShippingDetails
            line={line}
            {...{
              getFieldDecorator,
              getFieldValue,
              setFieldsValue,
              resetFields,
              type: 'Quote',
            }}
          />

          <Row>
            <Row>
              <ProductSelection
                doc={line}
                {...{
                  getFieldDecorator,
                  getFieldsValue,
                  getFieldValue,
                  setFieldsValue,
                  resetFields,
                  company,
                }}
              />
            </Row>
            {!isProduct && (
              <>
                {/*<Col span={12}>*/}
                {/*  <Form.Item*/}
                {/*    {...defaultFormItemLayout}*/}
                {/*    label={*/}
                {/*      <InfoTooltip*/}
                {/*        value={'Service'}*/}
                {/*        title={'Select a contract with rates'}*/}
                {/*      />*/}
                {/*    }>*/}
                {/*    {getFieldDecorator('ref.key', {*/}
                {/*      initialValue: get(line, 'ref.key'),*/}
                {/*      rules: [*/}
                {/*        {required: true, message: 'Please Select a Service!'},*/}
                {/*      ],*/}
                {/*    })(*/}
                {/*      <ServicesSelectCmp*/}
                {/*        placeholder={'Services'}*/}
                {/*        keyAtr={'key'}*/}
                {/*        valueAtr={'name'}*/}
                {/*        // onChange={onChangeService}*/}
                {/*      />,*/}
                {/*    )}*/}
                {/*  </Form.Item>*/}
                {/*</Col>*/}

                {/*<Col span={12}>*/}
                {/*  <Form.Item {...defaultFormItemLayout} label={'Rate'}>*/}
                {/*    {getFieldDecorator('rate', {*/}
                {/*      initialValue: get(line, 'rate'),*/}
                {/*    })(*/}
                {/*      <InputNumber*/}
                {/*        disabled={true}*/}
                {/*        style={{width: '100%'}}*/}
                {/*        placeholder={'Rate'}*/}
                {/*      />,*/}
                {/*    )}*/}
                {/*  </Form.Item>*/}
                {/*</Col>*/}

                {/*<Col span={12}>*/}
                {/*  <Form.Item {...defaultFormItemLayout} label={'Description'}>*/}
                {/*    <span>{strVal(service, 'product.description')}</span>*/}
                {/*  </Form.Item>*/}
                {/*</Col>*/}
              </>
            )}
          </Row>

          {!isEmpty(vendorQuoteLine) && (
            <>
              <FormSectionTitle title={'PAYMENT TERMS'} />
              <Row>
                <Col span={12}>
                  <h3>Vendor Payment Terms</h3>
                  <Form.Item style={{ marginRight: 10 }} label={'Vendor Delivery (DARO)'} {...LabelItemLayout}>
                    <span>{strVal(vendorQuoteLine, 'delivery.no')} Days</span>
                  </Form.Item>
                  {vendorQuotePaymentTermsLines.map((pt) => (
                    <Form.Item style={{ marginRight: 10 }} label={`Payment ${pt.no}`} {...LabelItemLayout}>
                      <span>
                        {pt.due.days} {pt.due.type} {pt.percentage} % Payment Due
                      </span>
                    </Form.Item>
                  ))}
                </Col>
                <Col span={12}>
                  <h3>Customer Payment Terms</h3>
                  <Form.Item label={'Customer Delivery (DARO)'} {...LabelItemLayout}>
                    <span>{strVal(quote, 'delivery.no')} Days</span>
                  </Form.Item>
                  {!isEmpty(QuotePaymentTermsLines) &&
                    QuotePaymentTermsLines.map((cpt) => (
                      <Form.Item label={`Payment ${cpt.no}`} {...LabelItemLayout}>
                        <span>
                          {cpt.due.days} {cpt.due.type} {cpt.percentage} % Payment Due
                        </span>
                      </Form.Item>
                    ))}
                </Col>
              </Row>
              <FinanceBreakDown doc={financeBreakDown} />
            </>
          )}
          {!isNoBid && (
            <>
              <Row>
                <h3>Finance Costs</h3>
                <Row style={{ background: '#f4f7e5' }}>
                  <Col span={8}>
                    <Form.Item {...threeFormItemLayout} label="From Currency">
                      {getFieldDecorator('from.currency', {
                        initialValue: get(line, 'from.currency') || get(vendorQuoteLine, 'spquote.currency'),
                        rules: rules.select,
                      })(<CurrencySelect disabled={!isEmpty(vendorQuoteLine)} style={{ width: '80%' }} />)}
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item {...threeFormItemLayout} label="To Currency">
                      {getFieldDecorator('to.currency', {
                        initialValue: get(line, 'to.currency') || quote.currency,
                        rules: rules.select,
                      })(
                        <CurrencySelect
                          disabled={getFieldValue('to.currency') === quote.currency}
                          style={{ width: '80%' }}
                        />
                      )}
                    </Form.Item>
                  </Col>

                  <CustomSpin spinning={fetchingCurrency} />
                  {!isEmpty(vendorQuoteLine) && (
                    <>
                      <Col span={8}>
                        <Form.Item label={'Buy Rate'} {...defaultFormItemLayout}>
                          <span>
                            {vendorQuote.currency} {vendorQuoteLine.rate}
                          </span>
                        </Form.Item>
                      </Col>

                      <Col span={8}>
                        <Form.Item {...defaultFormItemLayout} labelCol={{ span: 6 }} label={'Custom Convert'}>
                          {getFieldDecorator('isCustomConversion', {
                            initialValue: get(line, 'isCustomConversion'),
                            valuePropName: 'checked',
                          })(<Checkbox />)}
                        </Form.Item>
                      </Col>

                      <Col span={8}>
                        <Form.Item {...defaultFormItemLayout} label={'Conversion Rate'}>
                          {getFieldDecorator('buildUp.cost.conversion', {
                            initialValue: get(line, 'buildUp.cost.conversion'),
                          })(
                            <Input
                              type="number"
                              precision={3}
                              style={{ width: '80%' }}
                              placeholder={'Conversion Rate'}
                              disabled={!getFieldValue('isCustomConversion')}
                            />
                          )}
                          <InfoTooltip title={'BuyCurrency to SellCurrency'} />
                        </Form.Item>
                      </Col>
                    </>
                  )}

                  <Col span={8}>
                    <Form.Item {...defaultFormItemLayout} label={'Finance Amount:'}>
                      {getFieldDecorator('buildUp.finance.converted', {
                        initialValue: get(line, 'buildUp.finance.converted'),
                        rules: rules.input,
                      })(
                        <Input
                          type="number"
                          addonBefore={<span>{strVal(quote, 'currency')}</span>}
                          style={{ width: '80%' }}
                          placeholder={'Amount Financed'}
                        />
                      )}
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item {...defaultFormItemLayout} label={'Finance Rate'}>
                      {getFieldDecorator('buildUp.finance.rate', {
                        initialValue: get(line, 'buildUp.finance.rate') || 12,
                      })(
                        <Input
                          type="number"
                          addonAfter={<span>% per Annum</span>}
                          precision={2}
                          style={{ width: '80%' }}
                          placeholder={'Finance Rate'}
                        />
                      )}
                      <InfoTooltip title={'Annual Finance % / 365 Days for FinanceDayRate'} />
                    </Form.Item>
                  </Col>

                  {showFinanceCost === true ? (
                    <>
                      <Col span={8}>
                        <Form.Item {...defaultFormItemLayout} label={'Finance Cost'}>
                          {getFieldDecorator('buildUp.finance.cost', {
                            initialValue: get(line, 'buildUp.finance.cost'),
                          })(
                            <Input
                              type="number"
                              addonBefore={<span>{strVal(quote, 'currency')}</span>}
                              style={{ width: '80%' }}
                              placeholder={'Finance Cost'}
                            />
                          )}
                          <InfoTooltip
                            title={
                              '[vendor(% of overall credit days x Total Credit Days x PaymentAmount x FinanceDayRate)]'
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        {!isEmpty(vendorQuoteLine) && (
                          <>
                            <Form.Item {...defaultFormItemLayout} label={'Finance Credit'}>
                              {getFieldDecorator('buildUp.finance.credit', {
                                initialValue: get(line, 'buildUp.finance.credit'),
                              })(
                                <Input
                                  type="number"
                                  addonBefore={<span>{strVal(quote, 'currency')}</span>}
                                  style={{ width: '80%' }}
                                  placeholder={'Finance Credit'}
                                />
                              )}
                              <InfoTooltip
                                title={
                                  '[customer(% of overall credit days x Total Credit Days x PaymentAmount x FinanceDayRate)]'
                                }
                              />
                            </Form.Item>
                          </>
                        )}
                      </Col>
                    </>
                  ) : (
                    <Col span={8}>
                      Finance cost is not applicable
                      <a
                        onClick={(e) => {
                          e.preventDefault()
                          setShowFinanceCost(true)
                        }}>
                        Show
                      </a>
                    </Col>
                  )}
                </Row>
              </Row>

              <Row>
                <h3>Pricing Build Up</h3>
                <Row style={{ background: '#dff4f7' }}>
                  <Col span={8}>
                    <Form.Item {...defaultFormItemLayout} label={'Item Cost:'}>
                      {getFieldDecorator('buildUp.cost.cost', {
                        initialValue: get(line, 'buildUp.cost.cost'),
                        rules: rules.input,
                      })(
                        <Input
                          type="number"
                          addonBefore={<span>{strVal(quote, 'currency')}</span>}
                          style={{ width: '80%' }}
                          placeholder={'Item Cost'}
                          // onChange={e => {
                          //   const value = numVal(e, 'target.value')
                          //   const sell = calcMargin(
                          //     value,
                          //     getFieldValue('margin'),
                          //   )
                          //   setFieldsValue({'buildUp.cost.sell': sell})
                          // }}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item {...defaultFormItemLayout} label={'Item Margin:'}>
                      {getFieldDecorator('buildUp.cost.margin', {
                        initialValue: get(line, 'buildUp.cost.margin') || mapMargin[quote.quoteType] || 0,
                        rules: rules.input,
                      })(
                        <Input
                          type="number"
                          addonAfter={<span>%</span>}
                          style={{ width: '80%' }}
                          placeholder={'Item Margin'}
                          min={0}
                          max={99.999}
                          precision={2}
                          // onChange={e => {
                          //   const value = numVal(e, 'target.value')
                          //   const rate = calcMargin(
                          //     getFieldValue('buildUp.cost.cost'),
                          //     value,
                          //   )
                          //   setFieldsValue({
                          //     'buildUp.cost.sell': rate,
                          //   })
                          // }}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item {...defaultFormItemLayout} label={'Item Sell:'}>
                      {getFieldDecorator('buildUp.cost.sell', {
                        initialValue: get(line, 'buildUp.cost.sell'),
                        rules: rules.input,
                      })(
                        <Input
                          disabled={true}
                          type="number"
                          addonBefore={<span>{strVal(quote, 'currency')}</span>}
                          style={{ width: '80%' }}
                          placeholder={'Item Sell Rate'}
                          precision={2}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item {...defaultFormItemLayout} label={'Finance Cost:'}>
                      {getFieldDecorator('buildUp.finance.difference', {
                        initialValue: get(line, 'buildUp.finance.difference'),
                      })(
                        <Input
                          type="number"
                          addonBefore={<span>{strVal(quote, 'currency')}</span>}
                          style={{ width: '80%' }}
                          placeholder={'Finance Cost'}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item {...defaultFormItemLayout} label={'Finance Margin:'}>
                      {getFieldDecorator('buildUp.finance.margin', {
                        initialValue: get(line, 'buildUp.finance.margin') || mapFinMargin[quote.quoteType] || 0,
                      })(
                        <Input
                          type="number"
                          addonAfter={<span>%</span>}
                          style={{ width: '80%' }}
                          placeholder={'Finance Margin'}
                          min={0}
                          max={100}
                          precision={2}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item {...defaultFormItemLayout} label={'Finance Sell:'}>
                      {getFieldDecorator('buildUp.finance.sell', {
                        initialValue: get(line, 'buildUp.finance.sell') || 0,
                      })(
                        <Input
                          disabled={true}
                          type="number"
                          addonBefore={<span>{strVal(quote, 'currency')}</span>}
                          style={{ width: '80%' }}
                          placeholder={'Finance Sell Rate'}
                          precision={2}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item {...defaultFormItemLayout} label={'Other Cost:'}>
                      {getFieldDecorator('buildUp.other.cost', {
                        initialValue: get(line, 'buildUp.other.cost') || 0,
                      })(
                        <Input
                          type="number"
                          addonBefore={<span>{strVal(quote, 'currency')}</span>}
                          style={{ width: '80%' }}
                          placeholder={'Other Cost'}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item {...defaultFormItemLayout} label={'Other Margin:'}>
                      {getFieldDecorator('buildUp.other.margin', {
                        initialValue: get(line, 'buildUp.other.margin') || 10,
                      })(
                        <Input
                          type="number"
                          addonAfter={<span>%</span>}
                          style={{ width: '80%' }}
                          placeholder={'Other Margin'}
                          min={0}
                          max={100}
                          precision={2}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item {...defaultFormItemLayout} label={'Other Sell:'}>
                      {getFieldDecorator('buildUp.other.sell', {
                        initialValue: get(line, 'buildUp.other.sell') || 0,
                      })(
                        <Input
                          disabled={true}
                          type="number"
                          addonBefore={<span>{strVal(quote, 'currency')}</span>}
                          style={{ width: '80%' }}
                          placeholder={'Other Sell Rate'}
                          precision={2}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={8} />
                  <Col span={8}>
                    <Button
                      style={{ float: 'right', margin: 10 }}
                      type={'primary'}
                      // onClick={calculate}
                      size={'small'}>
                      Calculate
                    </Button>
                  </Col>
                  <Col span={8}>
                    <Form.Item {...defaultFormItemLayout} label={'Sub-Total:'}>
                      {getFieldDecorator('rate', {
                        initialValue: get(line, 'rate'),
                      })(
                        <Input
                          disabled={true}
                          type="number"
                          addonBefore={<span>{strVal(quote, 'currency')}</span>}
                          style={{ width: '80%', fontWeight: 'bold' }}
                          placeholder={'Sub-Total'}
                          precision={2}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={8} />
                  <Col span={8}>
                    <Form.Item {...defaultFormItemLayout} label={'Quantity'}>
                      {getFieldDecorator('unit', {
                        initialValue: get(line, 'unit'),
                        rules: [{ required: true, message: 'Please Select a unit!' }],
                      })(
                        <Input
                          type="number"
                          addonAfter={<span>{get(line, 'uom.code') || get(line, 'productLine.uom.key')}</span>}
                          style={{ width: '80%' }}
                          placeholder={'Quantity'}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item style={{ fontWeight: 'bold' }} {...defaultFormItemLayout} label={'Total (GROSS)'}>
                      <span style={{ fontWeight: 'bold' }}>
                        {quote.currency} {moneyInline(total.gross)}
                      </span>
                    </Form.Item>
                  </Col>
                  {get(quote, 'taxType') !== 'ex-tax' && (
                    <>
                      <Col span={8}>
                        <Form.Item {...defaultFormItemLayout} label={'Tax Type'}>
                          {getFieldDecorator('tax.type', {
                            initialValue: get(line, 'tax.type'),
                            rules: [
                              {
                                required: true,
                                message: 'Please Select a Tax Type!',
                              },
                            ],
                          })(<TaxTypeSelect style={{ width: '80%' }} />)}
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item {...defaultFormItemLayout} label={'Tax Percentage'}>
                          {getFieldDecorator('tax.percentage', {
                            initialValue: get(line, 'tax.percentage'),
                            rules: ['STD', 'CUS'].includes(getFieldValue('tax.type'))
                              ? rules.moreThanZero
                              : rules.equalToZero,
                          })(
                            <Input
                              type={'number'}
                              addonAfter={<span>%</span>}
                              style={{ width: '80%' }}
                              placeholder={'Tax Percentage'}
                            />
                          )}
                        </Form.Item>
                        <Form.Item {...defaultFormItemLayout} label={'Tax Rate'}>
                          {getFieldDecorator('tax.rate', {
                            initialValue: get(line, 'tax.rate'),
                          })(
                            <Input
                              hidden
                              type={'number'}
                              style={{ width: '80%' }}
                              placeholder={'Tax Rate'}
                            />
                          )}
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item style={{ fontWeight: 'bold' }} {...defaultFormItemLayout} label={'Total (TAX)'}>
                          <span style={{ fontWeight: 'bold' }}>
                            {quote.currency} {moneyInline(total.tax)}
                          </span>
                        </Form.Item>
                      </Col>
                      <Col span={8} />
                      <Col span={8} />
                      <Col span={8}>
                        <Form.Item style={{ fontWeight: 'bold' }} {...defaultFormItemLayout} label={'Total (NET)'}>
                          <span style={{ fontWeight: 'bold' }}>
                            {quote.currency} {moneyInline(total.net)}
                          </span>
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>
              </Row>
            </>
          )}
          {get(category, 'code') === 'vehicles' && (
            <Row>
              <Row style={{ marginLeft: 10 }}>
                <h3>Pick up / Drop off</h3>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'Pick Up'}>
                    <Col span={12}>
                      {getFieldDecorator('booking.pickup.date', {
                        rules: [
                          {
                            required: true,
                            message: 'Please Select a Pick Up!',
                          },
                        ],
                        initialValue: dateVal(line, 'booking.pickup.date'),
                      })(
                        <DatePicker
                          style={{ width: '100%' }}
                          // disabledDate={disabledStartDate}
                          // onChange={onStartChange}
                          // onOpenChange={handleStartOpenChange}
                          placeholder="Pick up Date"
                          format="ddd YYYY-MM-DD"
                        />
                      )}
                    </Col>
                    <Col span={12}>
                      <Form.Item>
                        {getFieldDecorator('booking.pickup.time', {
                          rules: [
                            {
                              required: true,
                              message: 'Please Select a pickup time!',
                            },
                          ],

                          initialValue: dateVal(line, 'booking.pickup.time'),
                        })(<TimePicker format="HH:mm" style={{ marginLeft: 10 }} placeholder="Pick up Time" />)}
                      </Form.Item>
                    </Col>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'Drop off'}>
                    <Col span={12}>
                      {getFieldDecorator('booking.dropoff.date', {
                        rules: [
                          {
                            required: true,
                            message: 'Please Select a Drop off date!',
                          },
                        ],
                        initialValue: dateVal(line, 'booking.dropoff.date'),
                      })(
                        <DatePicker
                          style={{ width: '100%' }}
                          // disabledDate={disabledEndDate}
                          // onChange={onEndChange}
                          // open={endOpen}
                          // onOpenChange={handleEndOpenChange}
                          placeholder="Drop off Date"
                          format="ddd YYYY-MM-DD"
                        />
                      )}
                    </Col>
                    <Col span={12}>
                      <Form.Item>
                        {getFieldDecorator('booking.dropoff.time', {
                          rules: [
                            {
                              required: true,
                              message: 'Please Select a drop off time!',
                            },
                          ],
                          initialValue: dateVal(line, 'booking.dropoff.time'),
                        })(<TimePicker format="HH:mm" style={{ marginLeft: 10 }} placeholder="Drop off Time" />)}
                      </Form.Item>
                    </Col>
                  </Form.Item>
                </Col>
              </Row>
              <h3>Vehicle</h3>
              <Row>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'Vehicle'}>
                    {getFieldDecorator('booking.vehicle._id', {
                      initialValue: get(line, 'booking.vehicle._id'),
                      rules: [{ required: true, message: 'Please Select a Vehicle!' }],
                    })(
                      <VehiclesSelect
                        placeholder={'Vehicles'}
                        keyAtr={'_id'}
                        valueAtr={'description'}
                        // onChange={onChangeVehicles}
                      />
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'Plate No'}>
                    {getFieldDecorator('booking.vehicle.plate', {
                      initialValue: get(line, 'booking.vehicle.plate'),
                    })(<Input placeholder={'Plate No'} />)}
                  </Form.Item>
                </Col>
              </Row>
              <h3>Salik</h3>
              <Row>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'Tag No'}>
                    {getFieldDecorator('booking.salik.tag', {
                      initialValue: get(line, 'booking.salik.tag'),
                    })(<Input placeholder={'Tag No'} />)}
                  </Form.Item>
                </Col>
              </Row>
            </Row>
          )}
          {get(category, 'code') === 'commuter-services' && (
            <Row>
              <Row style={{ marginLeft: 10 }}>
                <h3>Start / End Date</h3>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'Start'}>
                    {getFieldDecorator('booking.pickup.date', {
                      rules: [{ required: true, message: 'Please Select a Start!' }],
                      initialValue: dateVal(line, 'booking.pickup.date'),
                    })(
                      <DatePicker
                        style={{ width: '100%' }}
                        // disabledDate={disabledStartDate}
                        // onChange={onStartChange}
                        // onOpenChange={handleStartOpenChange}
                        placeholder="Start Date"
                        format="ddd YYYY-MM-DD"
                      />
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'End'}>
                    {getFieldDecorator('booking.dropoff.date', {
                      rules: [
                        {
                          required: true,
                          message: 'Please Select a End date!',
                        },
                      ],
                      initialValue: dateVal(line, 'booking.dropoff.date'),
                    })(
                      <DatePicker
                        style={{ width: '100%' }}
                        // disabledDate={disabledEndDate}
                        // onChange={onEndChange}
                        // open={endOpen}
                        // onOpenChange={handleEndOpenChange}
                        placeholder="End Date"
                        format="ddd YYYY-MM-DD"
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Row>
          )}
          {get(category, 'code') === 'chauffeur-services' && (
            <Row>
              <Row>
                <Col offset={2} span={9}>
                  <Card title={'Pickup details'}>
                    <Form.Item label={'Date / (Time - optional)'}>
                      {getFieldDecorator('booking.pickup.date', {
                        initialValue: dateVal(line, 'booking.pickup.date'),
                        rules: [{ required: true, message: 'Please Select a Date!' }],
                      })(<DatePicker style={{ width: '100%' }} placeholder="Pick up Date" format="ddd YYYY-MM-DD" />)}
                      {getFieldDecorator('booking.pickup.time', {
                        initialValue: dateVal(line, 'booking.pickup.time'),
                      })(<TimePicker format="HH:mm" style={{ marginLeft: 10 }} placeholder="Pick up Time" />)}
                    </Form.Item>
                    <Form.Item label={'Notes'}>
                      {getFieldDecorator('booking.pickup.notes', {
                        initialValue: get(line, 'booking.pickup.notes'),
                      })(<Input placeholder="Notes" />)}
                    </Form.Item>

                    <Form.Item label={'Location'}>
                      {getFieldDecorator('booking.pickup.location', {
                        initialValue: get(line, 'booking.pickup.location'),
                        rules: [
                          {
                            required: true,
                            message: 'Location Address Can`\t be Empty.',
                          },
                        ],
                      })(
                        <LocationSelect
                        // onChange={v => {
                        //   setFieldsValue({
                        //     'booking.pickup.label': v.address,
                        //   })
                        //   handleLocationChange('booking.pickup.location', v)
                        // }}
                        />
                      )}
                    </Form.Item>
                    <Form.Item label={'Display Address'}>
                      {getFieldDecorator('booking.pickup.label', {
                        initialValue: get(line, 'booking.pickup.label'),
                      })(<Input placeholder={'Display Address'} />)}
                    </Form.Item>
                  </Card>
                </Col>
                <Col offset={2} span={9}>
                  <Card title={'Drop off details'}>
                    <Form.Item label={'Notes'}>
                      {getFieldDecorator('booking.dropoff.notes', {
                        initialValue: get(line, 'booking.dropoff.notes'),
                      })(<Input placeholder="Notes" />)}
                    </Form.Item>

                    <Form.Item label={'Location'}>
                      {getFieldDecorator('booking.dropoff.location', {
                        initialValue: get(line, 'booking.dropoff.location'),
                        rules: [
                          {
                            required: true,
                            message: 'Location  Can`\t be Empty.',
                          },
                        ],
                      })(
                        <LocationSelect
                        // onChange={v => {
                        //   setFieldsValue({
                        //     'booking.dropoff.label': v.address,
                        //   })
                        //   handleLocationChange('booking.dropoff.location', v)
                        // }}
                        />
                      )}
                    </Form.Item>
                    <Form.Item label={'Display Address'}>
                      {getFieldDecorator('booking.dropoff.label', {
                        initialValue: get(line, 'booking.dropoff.label'),
                      })(<Input placeholder={'Display Address'} />)}
                    </Form.Item>
                  </Card>
                </Col>
              </Row>
              <Row>
                <Col style={{ marginTop: 10 }} offset={2} span={20}>
                  <Card
                    title={<InfoTooltip value={'Destinations'} title={'Locations of other trips within this line.'} />}
                    extra={<Button type="primary" shape="circle" icon="plus" onClick={addDestination} />}>
                    {/*  {destinations.map(d => (*/}
                    {/*    <Fragment>*/}
                    {/*      <Form.Item*/}
                    {/*        {...defaultFormItemLayout}*/}
                    {/*        label={'Destination'}>*/}
                    {/*        {getFieldDecorator('booking.destinations.' + d.key, {*/}
                    {/*          initialValue: d.location,*/}
                    {/*          rules: [*/}
                    {/*            {*/}
                    {/*              required: true,*/}
                    {/*              message: `Destination Can't be Empty.`,*/}
                    {/*            },*/}
                    {/*          ],*/}
                    {/*        })(*/}
                    {/*          <LocationSelect*/}
                    {/*            key={d.key}*/}
                    {/*            style={{width: '80%'}}*/}
                    {/*            onChange={v => {*/}
                    {/*              onChangeDestinations(d.key, v.address, 'label')*/}
                    {/*              onChangeDestinations(d.key, v, 'location')*/}
                    {/*            }}*/}
                    {/*          />,*/}
                    {/*        )}*/}
                    {/*      </Form.Item>*/}
                    {/*      <Form.Item*/}
                    {/*        {...defaultFormItemLayout}*/}
                    {/*        label={'Display Address'}>*/}
                    {/*        <Input*/}
                    {/*          style={{width: '80%'}}*/}
                    {/*          placeholder={'Display Address'}*/}
                    {/*          value={d.label}*/}
                    {/*          onChange={v =>*/}
                    {/*            onChangeDestinations(*/}
                    {/*              d.key,*/}
                    {/*              v.target.value,*/}
                    {/*              'label',*/}
                    {/*            )*/}
                    {/*          }*/}
                    {/*        />*/}
                    {/*        {*/}
                    {/*          <Button*/}
                    {/*            style={{marginLeft: 10}}*/}
                    {/*            type="danger"*/}
                    {/*            shape="circle"*/}
                    {/*            icon="minus"*/}
                    {/*            onClick={() => removeDestination(d.key)}*/}
                    {/*          />*/}
                    {/*        }*/}
                    {/*      </Form.Item>*/}
                    {/*    </Fragment>*/}
                    {/*  ))}*/}
                  </Card>
                </Col>
              </Row>
            </Row>
          )}
          {get(category, 'code') === 'VD' && (
            <Row>
              <Row style={{ marginLeft: 10 }}>
                <h3>Booking Details</h3>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'Start'}>
                    {getFieldDecorator('booking.pickup.date', {
                      rules: [{ required: true, message: 'Please Select a Start!' }],
                      initialValue: dateVal(line, 'booking.pickup.date'),
                    })(
                      <DatePicker
                        style={{ width: '100%' }}
                        // disabledDate={disabledStartDate}
                        // onChange={onStartChange}
                        // onOpenChange={handleStartOpenChange}
                        placeholder="Start Date"
                        format="ddd YYYY-MM-DD"
                      />
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'End'}>
                    {getFieldDecorator('booking.dropoff.date', {
                      initialValue: dateVal(line, 'booking.dropoff.date'),
                    })(
                      <DatePicker
                        style={{ width: '100%' }}
                        // disabledDate={disabledEndDate}
                        // onChange={onEndChange}
                        // open={endOpen}
                        // onOpenChange={handleEndOpenChange}
                        placeholder="End Date"
                        format="ddd YYYY-MM-DD"
                      />
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'Days'}>
                    {/*<span>{days}</span>*/}
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <h3>Vehicle</h3>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'Vehicle'}>
                    {getFieldDecorator('booking.vehicle.name', {
                      initialValue: get(line, 'booking.vehicle.name'),
                    })(<Input placeholder={'Vehicles'} />)}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'Plate No'}>
                    {getFieldDecorator('booking.vehicle.plate', {
                      initialValue: get(line, 'booking.vehicle.plate'),
                    })(<Input placeholder={'Plate No'} />)}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'Driver Name'}>
                    {getFieldDecorator('booking.vehicle.driver', {
                      initialValue: get(line, 'booking.vehicle.driver'),
                    })(<Input placeholder={'Driver Name'} />)}
                  </Form.Item>
                </Col>
              </Row>
            </Row>
          )}
          {['PM', 'VR', 'PV', 'LB'].includes(get(category, 'code')) && (
            <Row>
              <Row style={{ marginLeft: 10 }}>
                <h3>Booking Details</h3>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'Start'}>
                    {getFieldDecorator('booking.pickup.date', {
                      rules: [{ required: true, message: 'Please Select a Start!' }],
                      initialValue: dateVal(line, 'booking.pickup.date'),
                    })(
                      <DatePicker
                        style={{ width: '100%' }}
                        // disabledDate={disabledStartDate}
                        // onChange={onStartChange}
                        // onOpenChange={handleStartOpenChange}
                        placeholder="Start Date"
                        format="ddd YYYY-MM-DD"
                      />
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'End'}>
                    {getFieldDecorator('booking.dropoff.date', {
                      initialValue: dateVal(line, 'booking.dropoff.date'),
                    })(
                      <DatePicker
                        style={{ width: '100%' }}
                        // disabledDate={disabledEndDate}
                        // onChange={onEndChange}
                        // open={endOpen}
                        // onOpenChange={handleEndOpenChange}
                        placeholder="End Date"
                        format="ddd YYYY-MM-DD"
                      />
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'Days'}>
                    {/*<span>{days}</span>*/}
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <h3>Vehicle</h3>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'Vehicle'}>
                    {getFieldDecorator('booking.vehicle.name', {
                      initialValue: get(line, 'booking.vehicle.name'),
                    })(<Input placeholder={'Vehicles'} />)}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item {...defaultFormItemLayout} label={'Plate No'}>
                    {getFieldDecorator('booking.vehicle.plate', {
                      initialValue: get(line, 'booking.vehicle.plate'),
                    })(<Input placeholder={'Plate No'} />)}
                  </Form.Item>
                </Col>
              </Row>
            </Row>
          )}
        </>
      )}
    </Form>
  )
}

QuoteLineForm.propTypes = {}
QuoteLineForm.defaultProps = {
  line: {},
  ServicesSelectCmp: Select,
  ProductsSelect: Select,
  formRef: { current: {} },
  addDestination: () => {},
  removeDestination: () => {},
  onChangeDestinations: () => {},
  handleLocationChange: () => {},
  type: 'new',
  destinations: [],
}

export default Form.create()(QuoteLineForm)
